// Trois soins autour de l'éditeur Monaco (Reps et challenges) que le wrapper
// @monaco-editor/react ne fait pas. Deux signalements d'apprenants sur PC
// (27/08 et 01/09/2026 : « le curseur se met tout à la fin du code pendant la
// saisie », « le curseur est décalé ») ont conduit à les écrire ; le contexte
// complet vit dans RepsCodeEditor (components/Reps/RepsExercise.jsx).

import { normaliserSaisieCode } from "@/lib/reps/saisieCode";

// LA SAISIE EST ASSAINIE AVANT TOUT LE RESTE (miroir du CodeEditorArea
// mobile, module lib/reps/saisieCode) : sur Safari iOS la « Ponctuation
// intelligente » du clavier transforme les guillemets tapés (chevrons « »
// + insécables au clavier FR, courbes au clavier EN), et sur desktop la même
// typographie arrive par collage — SyntaxError sur un code que l'apprenant
// voit juste. Monaco est corrigé par des éditions CIBLÉES ligne par ligne,
// jamais par la prop `value` : le wrapper remplacerait alors le modèle ENTIER
// (executeEdits plein) et le curseur sauterait en fin de fichier.
//
// Les lignes viennent du MODÈLE (getLinesContent, getLineMaxColumn), jamais
// d'un découpage de getValue() sur "\n". Sur Windows, Monaco crée en CRLF
// tout modèle sans saut de ligne (starter vide ou d'une ligne, fichier créé
// par l'apprenant) ; découpée sur "\n", chaque ligne non finale gardait un
// "\r" que Monaco retransformait en saut de ligne : une ligne fantôme par
// ligne corrigée, un modèle qui divergeait de l'état React, et le curseur qui
// sautait en fin de code au rendu suivant. `propre` se découpe sur /\r?\n/
// pour la même raison.
//
// Renvoie le contenu RÉEL du modèle après correction (ce que React doit
// mémoriser), ou null sans modèle.
export function corrigerSaisieMonaco(editor, propre) {
    const model = editor && editor.getModel ? editor.getModel() : null;
    if (!model) return null;
    const cible = String(propre == null ? "" : propre);
    if (model.getValue() === cible) return cible;
    const lignesAvant = model.getLinesContent();
    const lignesApres = cible.split(/\r?\n/);
    // La normalisation ne touche jamais aux sauts de ligne, les deux textes
    // ont donc le même nombre de lignes ; si un jour ce n'est plus vrai, on
    // préfère un remplacement brut à des éditions désalignées.
    if (lignesAvant.length !== lignesApres.length) {
        model.setValue(cible);
        return model.getValue();
    }
    const pos = editor.getPosition();
    const selections = editor.getSelections();
    const edits = [];
    for (let i = 0; i < lignesAvant.length; i++) {
        if (lignesAvant[i] === lignesApres[i]) continue;
        edits.push({
            range: {
                startLineNumber: i + 1,
                startColumn: 1,
                endLineNumber: i + 1,
                endColumn: model.getLineMaxColumn(i + 1),
            },
            text: lignesApres[i],
        });
    }
    if (edits.length) editor.executeEdits("reps-saisie", edits);
    // Le curseur reste où l'apprenant tape : sa colonne est recalculée en
    // normalisant le préfixe de sa ligne (la paire chevron + insécable
    // raccourcit la chaîne, la colonne brute pointerait un cran trop loin).
    // En multi-curseur (Alt+clic, Cmd+D), setPosition SUPPRIMERAIT les
    // curseurs secondaires que les éditions ci-dessus ont justement préservés
    // (Monaco les suit par tracked ranges) : on ne recalcule que le curseur
    // solitaire.
    if (
        (!selections || selections.length === 1) &&
        pos &&
        lignesAvant[pos.lineNumber - 1] !== undefined &&
        lignesAvant[pos.lineNumber - 1] !== lignesApres[pos.lineNumber - 1]
    ) {
        const prefixe = normaliserSaisieCode(
            lignesAvant[pos.lineNumber - 1].slice(0, pos.column - 1)
        );
        editor.setPosition({
            lineNumber: pos.lineNumber,
            column: prefixe.length + 1,
        });
    }
    return model.getValue();
}

// Sur Windows, Monaco crée en CRLF tout modèle qui n'a pas encore de saut de
// ligne (starter vide ou d'une seule ligne, fichier créé par l'apprenant).
// Les deux correcteurs tolèrent "\r\n" (grade.js, RepsGrader.php), mais un
// même code doit peser pareil quel que soit l'ordinateur : on repasse en LF
// à la création, avant la première frappe et hors pile d'annulation (setEOL,
// pas pushEOL). Le wrapper crée les modèles des autres onglets plus tard,
// d'où l'écoute de onDidCreateModel, posée une fois par page.
let creationEcoutee = false;
export function forcerFinsDeLigneLF(editor, monaco) {
    if (!editor || !monaco || !monaco.editor) return;
    const LF = monaco.editor.EndOfLineSequence.LF;
    const enLF = (model) => {
        if (model && !model.isDisposed() && model.getEOL() !== "\n") model.setEOL(LF);
    };
    enLF(editor.getModel());
    if (creationEcoutee) return;
    creationEcoutee = true;
    monaco.editor.onDidCreateModel(enLF);
}

// Chrome/Edge ≥ 121 : Monaco 0.55 saisit via l'API EditContext, et son nœud
// caché `.native-edit-context` reçoit un focus() nu, sans la garde de
// défilement du chemin textarea (vscode #324758, ouvert). Quand ce nœud est
// hors de la partie visible d'un ancêtre qui défile (la colonne de
// l'exercice), le navigateur fait défiler cet ancêtre pour le révéler, entre
// le test de clic et la pose du curseur : la colonne saute et le curseur
// tombe sur une autre ligne que celle cliquée. `preventScroll` rend au focus
// l'innocuité du chemin textarea ; Monaco révèle lui-même son curseur. À
// rejouer quand la configuration change (le nœud est recréé si le support
// d'accessibilité bascule) : idempotent.
export function neutraliserDefilementAuFocus(editor) {
    const racine = editor && editor.getDomNode ? editor.getDomNode() : null;
    if (!racine || !racine.querySelector) return;
    const noeud = racine.querySelector(".native-edit-context");
    if (!noeud || noeud.dataset.repsFocus === "1") return;
    noeud.dataset.repsFocus = "1";
    const focusNatif = noeud.focus;
    noeud.focus = function (options) {
        return focusNatif.call(this, { ...(options || {}), preventScroll: true });
    };
}
